/**
 * Phase 3B — Live Split Billing Governance Page (visibility only)
 * ───────────────────────────────────────────────────────────────────
 * Admin-only read-only surface for the Phase 3 Live Split Billing
 * engine. Consumes ONLY read endpoints. There are NO activation
 * buttons, NO funding triggers, NO claim activations, NO tenant
 * auto-enable controls.
 *
 * Backend endpoints:
 *   GET /api/admin/allocations/live/health
 *   GET /api/admin/allocations/live/flags
 *   GET /api/admin/allocations/live/rollout-status
 *   GET /api/admin/allocations/live/rollback/health
 *   GET /api/admin/allocations/live/reconciliation/status
 *   GET /api/admin/allocations/live/scaffold-info
 *   GET /api/admin/allocations/live/phase3-status   (Phase 3E — for status pill)
 *
 * Permission required: `allocation.live_split_billing.view`
 */
import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  ShieldAlert,
  ShieldCheck,
  RefreshCcw,
  CheckCircle2,
  XCircle,
  Lock,
  Info,
  AlertTriangle,
  HelpCircle,
} from 'lucide-react';

// ───────────────────── Phase 3F — Safety Status Pill ─────────────────────
// Compact colour-coded summary of platform_health.phase3_status (read-only).
// Auto-refresh every 60 seconds. Fail-closed: any error → UNKNOWN_ERROR.

const PILL_STYLES = {
  SAFE_SCAFFOLD: {
    border: 'border-emerald-300',
    bg: 'bg-emerald-50',
    text: 'text-emerald-800',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    icon: ShieldCheck,
    label: 'SAFE — Scaffold guard engaged',
  },
  WARNING_FLAGS_ON_BUT_SCAFFOLD_BLOCKING: {
    border: 'border-amber-300',
    bg: 'bg-amber-50',
    text: 'text-amber-900',
    badge: 'bg-amber-100 text-amber-900 border-amber-300',
    icon: AlertTriangle,
    label: 'WARNING — Phase 3 flag(s) ON but scaffold still blocking',
  },
  UNSAFE_SCAFFOLD_DISABLED: {
    border: 'border-red-400',
    bg: 'bg-red-50',
    text: 'text-red-900',
    badge: 'bg-red-100 text-red-900 border-red-400',
    icon: ShieldAlert,
    label: 'CRITICAL — Scaffold guard DISABLED',
  },
  UNKNOWN_ERROR: {
    border: 'border-slate-400',
    bg: 'bg-slate-100',
    text: 'text-slate-800',
    badge: 'bg-slate-200 text-slate-800 border-slate-400',
    icon: HelpCircle,
    label: 'UNKNOWN — Safety posture could not be determined',
  },
};

function formatRelative(iso) {
  if (!iso) return '—';
  try {
    const then = new Date(iso).getTime();
    const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch {
    return '—';
  }
}

function Phase3SafetyPill() {
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/allocations/live/phase3-status');
      setSnapshot(res?.data?.data || null);
      setError(false);
    } catch (e) {
      // Fail-closed: never imply safe; surface UNKNOWN_ERROR explicitly.
      setSnapshot(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000); // 60s auto-refresh
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine effective status, defaulting to UNKNOWN_ERROR on any
  // failure or missing snapshot.
  const status = error || !snapshot ? 'UNKNOWN_ERROR' : snapshot.status;
  const style = PILL_STYLES[status] || PILL_STYLES.UNKNOWN_ERROR;
  const Icon = style.icon;

  return (
    <Card
      className={`${style.border} ${style.bg}`}
      data-testid="phase3-safety-pill"
      data-testid-status={status}
    >
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <Icon className={`h-5 w-5 mt-0.5 ${style.text}`} />
            <div className="min-w-0">
              <div className={`text-sm font-semibold ${style.text}`} data-testid="phase3-pill-label">
                {style.label}
              </div>
              <div className={`text-xs mt-1 ${style.text} opacity-80`} data-testid="phase3-pill-message">
                {error
                  ? 'Phase 3 safety endpoint could not be reached. Defaulting to UNKNOWN posture. Do not assume safe activation.'
                  : (snapshot?.message || 'No snapshot available.')}
              </div>
              <div className={`text-[11px] mt-1 ${style.text} opacity-70 italic`} data-testid="phase3-pill-action">
                Action: {error
                  ? 'Investigate API connectivity and audit log for recent changes.'
                  : (snapshot?.recommended_action || '—')}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge
              variant="outline"
              className={`${style.badge} font-semibold whitespace-nowrap`}
              data-testid="phase3-pill-status-badge"
            >
              {status}
            </Badge>
            <Badge
              variant="outline"
              className="text-[10px] bg-white border-slate-200 text-slate-600 whitespace-nowrap"
              data-testid="phase3-pill-risk-badge"
            >
              Risk: {snapshot?.risk_level || 'UNKNOWN'}
            </Badge>
            <div className="flex items-center gap-1">
              <span
                className={`text-[10px] ${style.text} opacity-70`}
                data-testid="phase3-pill-checked-at"
                title={snapshot?.checked_at || ''}
              >
                last checked {formatRelative(snapshot?.checked_at)}
              </span>
              <button
                onClick={load}
                disabled={loading}
                title="Refresh now"
                className="text-[10px] underline hover:no-underline opacity-70 hover:opacity-100"
                data-testid="phase3-pill-refresh"
              >
                <RefreshCcw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
        <div className={`text-[10px] mt-3 ${style.text} opacity-60`}>
          Scaffold guard:&nbsp;
          <span data-testid="phase3-pill-scaffold-value">
            {snapshot?.scaffold_guard_enabled === undefined
              ? '?'
              : snapshot.scaffold_guard_enabled ? 'TRUE (engaged)' : 'FALSE (DISABLED)'}
          </span>
          &nbsp;·&nbsp;Phase {snapshot?.phase || '—'} v{snapshot?.scaffold_version || '—'}
          &nbsp;·&nbsp;app {snapshot?.app_version || 'unknown'}
          &nbsp;·&nbsp;auto-refresh 60s
        </div>
      </CardContent>
    </Card>
  );
}


const FLAG_LABELS = {
  multi_service_allocations_enabled: 'Umbrella programme',
  live_split_billing_enabled: 'Live split billing',
  live_split_billing_allowlist_enabled: 'Allowlist gating',
  live_split_billing_reconciliation_enforced: 'Reconciliation enforced',
};

function FlagRow({ label, value }) {
  const on = value === true;
  return (
    <div
      className="flex items-center justify-between py-2 border-b last:border-b-0"
      data-testid={`live-flag-row-${label}`}
    >
      <span className="text-sm text-slate-700">{label}</span>
      <Badge
        variant="outline"
        className={
          on
            ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
            : 'border-slate-300 text-slate-600 bg-slate-50'
        }
        data-testid={`live-flag-value-${label}`}
      >
        {on ? 'ON' : 'OFF'}
      </Badge>
    </div>
  );
}

export default function LiveSplitBillingGovernancePage() {
  const { hasPermission } = useAuth();
  const allowed = hasPermission('allocation.live_split_billing.view');

  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/allocations/live/health');
      setHealth(res?.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  if (!allowed) {
    return (
      <div className="p-6" data-testid="live-governance-no-permission">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Access denied
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">
              You don't have the <code>allocation.live_split_billing.view</code> permission.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const flags = health?.flags || {};
  const rollout = health?.rollout || {};
  const rollback = health?.rollback || {};
  const recon = health?.reconciliation || {};
  const tenantEnabled = Boolean(health?.tenant_enabled);
  const scaffoldActive = Boolean(health?.scaffold_active);
  const mutationEndpointsActive = Boolean(health?.mutation_endpoints_active);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="live-governance-page">
      {/* Phase 3F — Safety status pill (top of page, auto-refresh, fail-closed) */}
      <Phase3SafetyPill />

      {/* Header banner — always visible, always loud about the scaffold state */}
      <Card className="border-amber-300 bg-amber-50" data-testid="live-governance-banner">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <ShieldAlert className="h-5 w-5" />
            Phase 3 — Live Split Billing — NON-OPERATIVE SCAFFOLD
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-amber-900 leading-relaxed">
            This page is <strong>visibility only</strong>. The Phase 3 engine is
            registered but every mutation path (promote, demote, funding
            deduction, claim activation) is hard-coded to refuse. No live
            financial behaviour is in effect.
          </p>
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            <Badge variant="outline" className="border-amber-400 text-amber-800 bg-amber-100" data-testid="live-banner-phase">
              Phase {health?.phase || '3B'}
            </Badge>
            <Badge variant="outline" className="border-amber-400 text-amber-800 bg-amber-100" data-testid="live-banner-version">
              Scaffold {health?.scaffold_version || '3B-1'}
            </Badge>
            <Badge
              variant="outline"
              className="border-emerald-400 text-emerald-800 bg-emerald-100"
              data-testid="live-banner-scaffold-active"
            >
              {scaffoldActive ? 'Scaffold guard: ENGAGED' : 'Scaffold guard: OFF'}
            </Badge>
            <Badge
              variant="outline"
              className="border-emerald-400 text-emerald-800 bg-emerald-100"
              data-testid="live-banner-mutations-blocked"
            >
              Mutation endpoints: {mutationEndpointsActive ? 'ACTIVE' : 'BLOCKED'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-slate-800">Live Split Billing Governance</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="live-governance-refresh"
        >
          <RefreshCcw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="pt-4 text-sm text-red-700" data-testid="live-governance-error">
            {error}
          </CardContent>
        </Card>
      )}

      {/* Flag state */}
      <Card data-testid="live-governance-flags-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Feature Flags
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(FLAG_LABELS).map((k) => (
            <FlagRow key={k} label={FLAG_LABELS[k]} value={flags?.[k]} />
          ))}
          <p className="text-xs text-slate-500 mt-3">
            All four flags default OFF and fail-closed. Any OFF flag blocks
            the entire chain.
          </p>
        </CardContent>
      </Card>

      {/* Tenant rollout state */}
      <Card data-testid="live-governance-rollout-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            {tenantEnabled ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : (
              <XCircle className="h-4 w-4 text-slate-400" />
            )}
            Tenant Rollout
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-slate-500 text-xs">Org</div>
              <div className="font-medium" data-testid="live-rollout-org">
                {rollout?.org_id || '—'}
              </div>
            </div>
            <div>
              <div className="text-slate-500 text-xs">Stage</div>
              <div className="font-medium" data-testid="live-rollout-stage">
                {rollout?.phase3_stage || 'OFF'}
              </div>
            </div>
            <div>
              <div className="text-slate-500 text-xs">Mode</div>
              <div className="font-medium" data-testid="live-rollout-mode">
                {rollout?.mode || 'OFF'}
              </div>
            </div>
            <div>
              <div className="text-slate-500 text-xs">Allowlisted shifts</div>
              <div className="font-medium" data-testid="live-rollout-allowlist-count">
                {Array.isArray(rollout?.allowlist_shifts)
                  ? rollout.allowlist_shifts.length
                  : 0}
              </div>
            </div>
          </div>
          {rollout?.note && (
            <p className="text-xs text-slate-500 mt-3 italic" data-testid="live-rollout-note">
              {rollout.note}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Reconciliation enforcement */}
      <Card data-testid="live-governance-recon-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="h-4 w-4" />
            Reconciliation Enforcement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 text-sm">
            <span>Status:</span>
            <Badge
              variant="outline"
              className={
                recon?.reconciliation_enforced
                  ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                  : 'border-slate-300 text-slate-600 bg-slate-50'
              }
              data-testid="live-recon-status"
            >
              {recon?.reconciliation_enforced ? 'ENFORCED' : 'NOT ENFORCED'}
            </Badge>
          </div>
          {recon?.note && (
            <p className="text-xs text-slate-500 mt-3 italic">{recon.note}</p>
          )}
        </CardContent>
      </Card>

      {/* Rollback / journal health */}
      <Card data-testid="live-governance-rollback-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Rollback Subsystem
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-3 text-sm">
            {['IN_FLIGHT', 'COMMITTED', 'COMPENSATED', 'COMPENSATION_FAILED', 'ABORTED'].map((s) => (
              <div key={s} className="border rounded p-2 text-center" data-testid={`live-journal-${s}`}>
                <div className="text-xs text-slate-500">{s.replace(/_/g, ' ')}</div>
                <div className="font-semibold text-lg">
                  {rollback?.journal_counts?.[s] ?? 0}
                </div>
              </div>
            ))}
          </div>
          {rollback?.note && (
            <p className="text-xs text-slate-500 mt-3 italic">{rollback.note}</p>
          )}
        </CardContent>
      </Card>

      {/* Note about what's forbidden */}
      <Card className="border-slate-200 bg-slate-50" data-testid="live-governance-forbidden-card">
        <CardContent className="pt-4 text-xs text-slate-600 space-y-1">
          <div className="font-semibold text-slate-700 mb-1">
            Strictly forbidden in Phase 3B:
          </div>
          <div>• Live promote / demote (refused at orchestrator)</div>
          <div>• Funding deduction</div>
          <div>• Claim creation / activation</div>
          <div>• Payroll mutation</div>
          <div>• Timesheet mutation</div>
          <div>• SIL mutation</div>
          <div>• Mobile attendance mutation</div>
          <div>• Auto-enable of any feature flag</div>
        </CardContent>
      </Card>
    </div>
  );
}
